import React, { useEffect, useRef } from 'react'

import { useThrottledTracker } from 'src/lib/js/hooks/useTracker'
import { TrackingEvents } from 'src/shared/components/common/tracking/TrackingEvents'
import {
  TOAST_LOCAL_CATERING_SEARCH_PATH,
  TOAST_LOCAL_SEARCH_PATH,
  TOAST_LOCAL_VENUES_SEARCH_PATH
} from 'src/public/js/siteUtilities'
import { useFlags } from 'src/shared/components/common/feature_flags/useFlags'

import { useCustomer } from 'public/components/online_ordering/CustomerContextCommon'
import DemoButton from 'public/components/toast_local/common/DemoButton'
import Footer from 'public/components/toast_local/common/Footer'
import ToastLocalHelmet from 'public/components/toast_local/common/ToastLocalHelmet'
import Nav from 'public/components/toast_local/common/nav/Nav'
import CuisineFarm from 'public/components/toast_local/splash_page/CuisineFarm'
import Hero from 'public/components/toast_local/splash_page/Hero'
import HeroLegacy from 'public/components/toast_local/splash_page/HeroLegacy'
import RXHotSpots from 'public/components/toast_local/splash_page/RXHotSpots'
import TTOPromo from 'public/components/toast_local/splash_page/TTOPromo'
import {
  MSAS,
  msaToUrlPath,
  MsaType
} from 'public/components/toast_local/splash_page/msas.data'
import { t } from 'src/translations'

export type SplashPageSearchMode = 'RESTAURANTS' | 'CATERING' | 'VENUES'

export const getSearchPathForMode = (
  mode: SplashPageSearchMode,
  msa: MsaType
): string => {
  switch (mode) {
    case 'CATERING':
      return `${TOAST_LOCAL_CATERING_SEARCH_PATH}/${msaToUrlPath(msa)}`
    case 'VENUES':
      return `${TOAST_LOCAL_VENUES_SEARCH_PATH}/${msaToUrlPath(msa)}`
    case 'RESTAURANTS':
    default:
      return TOAST_LOCAL_SEARCH_PATH
  }
}

const Splash = ({ mode }: { mode: SplashPageSearchMode }) => {
  const { track } = useThrottledTracker()
  const splashPageViewedRef = useRef(false)
  const { loadingCustomer } = useCustomer()

  const { nvsCateringDiscoveryToastLocal } = useFlags()

  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      !splashPageViewedRef.current &&
      !loadingCustomer
    ) {
      track(TrackingEvents.LOCAL_HOME_VIEW, {})
      splashPageViewedRef.current = true
    }
  }, [track, loadingCustomer])

  return (
    <>
      <ToastLocalHelmet />
      <div className='toastLocalSplash'>
        <div className='defaultTemplate'>
          <Nav />
          {nvsCateringDiscoveryToastLocal ? (
            <Hero mode={mode} />
          ) : (
            <div className='paddedSection'>
              <HeroLegacy />
            </div>
          )}
          {mode === 'RESTAURANTS' && (
            <>
              <div className='paddedSection'>
                <CuisineFarm numCuisines={12} />
              </div>
              <div className='paddedSection'>
                <TTOPromo />
              </div>
            </>
          )}
          <div className='paddedSection'>
            <div className='posPromo'>
              <div className='message'>{t('splash.represent-restaurant')}</div>
              <div>
                <DemoButton buttonLocation='splash' />
              </div>
            </div>
          </div>
          <div className='paddedSection'>
            <RXHotSpots
              title={t('splash.more-to-explore')}
              iconProps={{
                src: 'toastLocal/splash/flame-orange-blue.svg',
                height: '60'
              }}
              elements={MSAS}
              getSearchPath={(msa) => getSearchPathForMode(mode, msa)}
            />
          </div>
          <Footer />
        </div>
      </div>
    </>
  )
}

export default Splash
